import { useEffect, useState } from 'react';
import api from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import { Webhook, Copy, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Small admin-only panel that exposes the JotForm webhook URL for the given
 * challenge. Includes secret masking, copy-to-clipboard, and JotForm setup hints.
 */
export const JotformWebhookPanel = ({ challengeId }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || url || !challengeId) return;
    setLoading(true);
    api.get(`/webhooks/jotform/judges/setup-url/${challengeId}`)
      .then((res) => {
        const { path, secret } = res.data || {};
        if (!path || !secret) {
          toast.error('Risposta webhook incompleta');
          return;
        }
        // Build the URL using the PUBLIC backend URL (visible to JotForm),
        // not the internal cluster hostname.
        const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, '');
        setUrl(`${base}${path}?secret=${secret}`);
      })
      .catch(() => toast.error('Errore nel recupero URL webhook'))
      .finally(() => setLoading(false));
  }, [open, challengeId, url]);

  const masked = url
    ? url.replace(/secret=[^&]+/, 'secret=••••••••••••••••••••••••')
    : '';

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL webhook copiato negli appunti');
    } catch {
      toast.error('Impossibile copiare. Seleziona manualmente.');
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/5" data-testid="jotform-webhook-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm hover:bg-white/5 rounded-lg"
        data-testid="jotform-webhook-toggle"
      >
        <span className="flex items-center gap-2">
          <Webhook size={14} className="text-primary" />
          <span className="font-medium">Webhook JotForm</span>
          <span className="text-xs text-muted-foreground">
            (incolla l'URL in JotForm per importare automaticamente i giurati)
          </span>
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={loading ? 'Caricamento…' : (revealed ? url : masked)}
              className="bg-white/5 border-white/10 font-mono text-xs"
              onFocus={(e) => e.target.select()}
              data-testid="jotform-webhook-url-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRevealed((v) => !v)}
              title={revealed ? 'Nascondi secret' : 'Mostra secret'}
              data-testid="jotform-webhook-reveal-btn"
              disabled={!url}
            >
              {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleCopy}
              disabled={!url}
              data-testid="jotform-webhook-copy-btn"
            >
              <Copy size={14} className="mr-1" />
              Copia
            </Button>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
            <li>In JotForm: <em>Settings → Integrations → Webhooks</em>, incolla l'URL completo.</li>
            <li>Mappa i campi del form a: <code>Email</code>, <code>Nome e Cognome</code>, <code>Job title</code>, <code>Azienda</code>, <code>Immagine profilo</code>, <code>Breve biografia</code>.</li>
            <li>Le immagini caricate su JotForm devono essere accessibili pubblicamente (Form Settings → Privacy).</li>
            <li>I giurati vengono creati senza email automatica: invia gli inviti manualmente dal pannello.</li>
          </ul>
        </div>
      )}
    </div>
  );
};
